import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus, Save, Trash2, Pencil, Sparkles, Eye, EyeOff, X, Palette,
} from "lucide-react";
import AutocompleteInput from "@/components/AutocompleteInput";

const PLACEHOLDERS = ["{rider}", "{sub_label}", "{category}", "{event}", "{date}"];

const PRESETS = [
  {
    name: "Royal Crown · Classic",
    enabled: true,
    intro_text: "ROYAL CROWN RODEO",
    intro_subtext: "Highlight Reel",
    intro_duration: 2.0,
    outro_text: "THANK YOU",
    outro_subtext: "See you at the next jackpot",
    outro_duration: 2.0,
    sponsor_logo_url: "",
    lower_third_template: "{rider} · {sub_label}",
    accent_color: "#f97316",
  },
  {
    name: "Event Showcase",
    enabled: true,
    intro_text: "{event}",
    intro_subtext: "{date}",
    intro_duration: 2.5,
    outro_text: "Full results online",
    outro_subtext: "",
    outro_duration: 1.5,
    sponsor_logo_url: "",
    lower_third_template: "{rider} · {sub_label}",
    accent_color: "#ea580c",
  },
  {
    name: "Rider Spotlight",
    enabled: true,
    intro_text: "{rider}",
    intro_subtext: "Featured Runs",
    intro_duration: 2.5,
    outro_text: "",
    outro_subtext: "",
    outro_duration: 0,
    sponsor_logo_url: "",
    lower_third_template: "{event} · {date}",
    accent_color: "#dc2626",
  },
];

export default function ReelTemplates() {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchAll = () => {
    setLoading(true);
    api.get("/reel-templates").then((r) => setTemplates(r.data.templates || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const addPreset = async (preset) => {
    await api.post("/reel-templates", preset);
    toast.success("Template added");
    fetchAll();
  };

  const toggleEnabled = async (t) => {
    await api.put(`/reel-templates/${t.id}`, { ...t, enabled: !t.enabled });
    fetchAll();
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    await api.delete(`/reel-templates/${t.id}`);
    toast.success("Deleted");
    fetchAll();
  };

  return (
    <div className="px-6 md:px-12 py-10 max-w-[1100px]" data-testid="reel-templates-page">
      <div className="flex items-start justify-between gap-4 mb-8 pb-5 border-b border-stone-800">
        <div className="border-l-4 border-orange-600 pl-5">
          <div className="label-tiny mb-2">Reel Templates</div>
          <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Reel Templates</h1>
          <p className="text-stone-400 mt-3">Pre-built intros, outros, and lower-thirds that can be applied when building a reel. Toggle on/off, edit, or create your own.</p>
        </div>
        <button onClick={() => { setEditing(null); setCreating(true); }} className="btn-primary inline-flex items-center gap-2" data-testid="new-template-btn">
          <Plus size={14} /> New Template
        </button>
      </div>

      {loading ? (
        <div className="text-stone-500 italic">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="border border-stone-800 p-8 text-center" data-testid="templates-empty">
          <Sparkles size={28} className="text-orange-500 mx-auto mb-3" />
          <div className="font-display uppercase text-2xl font-bold">No templates yet</div>
          <div className="text-stone-400 text-sm mt-2 mb-5">Start with one of these presets, then edit to taste.</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
            {PRESETS.map((p) => (
              <button key={p.name} onClick={() => addPreset(p)} className="border border-stone-800 hover:border-orange-700 p-4 text-left transition-colors" data-testid={`preset-${p.name}`}>
                <div className="font-display uppercase font-bold text-lg">{p.name}</div>
                <div className="text-[11px] text-stone-500 mt-2 line-clamp-3">
                  {p.intro_text} · {p.lower_third_template} {p.outro_text && `· ${p.outro_text}`}
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {templates.map((t) => (
            <TemplateCard key={t.id} template={t} onEdit={() => { setCreating(false); setEditing(t); }} onToggle={() => toggleEnabled(t)} onDelete={() => remove(t)} />
          ))}
          {/* Add preset shortcut */}
          {templates.length > 0 && PRESETS.filter((p) => !templates.find((t) => t.name === p.name)).length > 0 && (
            <div className="border border-dashed border-stone-700 p-4 col-span-full">
              <div className="label-tiny mb-2">Add a preset</div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.filter((p) => !templates.find((t) => t.name === p.name)).map((p) => (
                  <button key={p.name} onClick={() => addPreset(p)} className="btn-secondary !py-1.5 !text-xs" data-testid={`add-preset-${p.name}`}>
                    + {p.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {(editing || creating) && (
        <TemplateEditModal
          template={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); fetchAll(); }}
        />
      )}
    </div>
  );
}

function TemplateCard({ template, onEdit, onToggle, onDelete }) {
  return (
    <div className={`border ${template.enabled ? "border-stone-800" : "border-stone-800/60 opacity-60"} hover:border-orange-700 transition-colors p-5`} data-testid={`template-card-${template.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 border-2" style={{ borderColor: template.accent_color, backgroundColor: `${template.accent_color}33` }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <div className="font-display uppercase font-bold text-xl truncate">{template.name}</div>
            {!template.enabled && <span className="text-[9px] uppercase tracking-widest text-stone-500 border border-stone-700 px-1.5 py-0.5">Off</span>}
          </div>
          {template.intro_text && (
            <div className="text-xs text-stone-400 mt-2">
              <span className="text-orange-500 uppercase tracking-wider mr-1.5">Intro</span>
              {template.intro_text}{template.intro_subtext ? ` · ${template.intro_subtext}` : ""}
            </div>
          )}
          {template.lower_third_template && (
            <div className="text-xs text-stone-400 mt-1">
              <span className="text-orange-500 uppercase tracking-wider mr-1.5">L3</span>
              {template.lower_third_template}
            </div>
          )}
          {template.outro_text && (
            <div className="text-xs text-stone-400 mt-1">
              <span className="text-orange-500 uppercase tracking-wider mr-1.5">Outro</span>
              {template.outro_text}{template.outro_subtext ? ` · ${template.outro_subtext}` : ""}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-1 mt-4 pt-4 border-t border-stone-800">
        <button onClick={onToggle} className="btn-secondary !py-1.5 !px-3 inline-flex items-center gap-1.5 text-xs" title={template.enabled ? "Disable" : "Enable"} data-testid={`toggle-${template.id}`}>
          {template.enabled ? <EyeOff size={12} /> : <Eye size={12} />}
          {template.enabled ? "Disable" : "Enable"}
        </button>
        <button onClick={onEdit} className="btn-secondary !py-1.5 !px-3 inline-flex items-center gap-1.5 text-xs hover:!text-orange-400" data-testid={`edit-${template.id}`}>
          <Pencil size={12} /> Edit
        </button>
        <button onClick={onDelete} className="btn-secondary !py-1.5 !px-2 ml-auto hover:!text-rose-400" data-testid={`delete-${template.id}`}>
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function TemplateEditModal({ template, onClose, onSaved }) {
  const [t, setT] = useState(template || {
    name: "",
    enabled: true,
    intro_text: "",
    intro_subtext: "",
    intro_duration: 2.0,
    outro_text: "",
    outro_subtext: "",
    outro_duration: 2.0,
    sponsor_logo_url: "",
    lower_third_template: "{rider} · {sub_label}",
    accent_color: "#f97316",
  });
  const [saving, setSaving] = useState(false);
  const isNew = !template?.id;

  const save = async () => {
    if (!t.name.trim()) {
      toast.error("Name required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) await api.post("/reel-templates", t);
      else await api.put(`/reel-templates/${template.id}`, t);
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved();
    } catch (err) {
      toast.error("Failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setT((prev) => ({ ...prev, [k]: v }));
  const insertVar = (field, v) => setField(field, (t[field] || "") + v);

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="template-edit-modal">
      <div className="bg-stone-950 border border-stone-700 max-w-2xl w-full max-h-[92vh] overflow-y-auto my-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-800 sticky top-0 bg-stone-950 z-10">
          <div className="font-display uppercase text-2xl font-bold">{isNew ? "New Template" : "Edit Template"}</div>
          <button onClick={onClose} className="btn-secondary" data-testid="template-modal-close"><X size={14} /></button>
        </div>
        <div className="p-6 space-y-5">
          <Field label="Name">
            <AutocompleteInput field="template_name" value={t.name} onChange={(v) => setField("name", v)} className="field-input" placeholder="My signature reel" data-testid="tmpl-name" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Accent Color">
              <div className="flex items-center gap-2">
                <input type="color" value={t.accent_color || "#f97316"} onChange={(e) => setField("accent_color", e.target.value)} className="w-12 h-10 bg-stone-900 border border-stone-700 cursor-pointer" data-testid="tmpl-color" />
                <input type="text" value={t.accent_color || ""} onChange={(e) => setField("accent_color", e.target.value)} className="field-input flex-1" />
              </div>
            </Field>
            <Field label="Status">
              <label className="flex items-center gap-2 text-sm pt-2.5">
                <input type="checkbox" checked={!!t.enabled} onChange={(e) => setField("enabled", e.target.checked)} data-testid="tmpl-enabled" />
                Enabled (selectable when building reels)
              </label>
            </Field>
          </div>

          <div className="border-t border-stone-800 pt-4">
            <div className="font-display uppercase text-lg font-bold mb-3">Intro Card</div>
            <Field label="Main Title">
              <AutocompleteInput field="intro_text" value={t.intro_text || ""} onChange={(v) => setField("intro_text", v)} placeholder="ROYAL CROWN RODEO" className="field-input" data-testid="tmpl-intro" />
            </Field>
            <Field label="Subtitle">
              <AutocompleteInput field="intro_subtext" value={t.intro_subtext || ""} onChange={(v) => setField("intro_subtext", v)} placeholder="Highlight Reel" className="field-input" data-testid="tmpl-intro-sub" />
            </Field>
            <Field label="Duration (seconds)">
              <input type="number" step="0.5" min="0" max="6" value={t.intro_duration} onChange={(e) => setField("intro_duration", parseFloat(e.target.value) || 0)} className="field-input !w-32" data-testid="tmpl-intro-dur" />
            </Field>
          </div>

          <div className="border-t border-stone-800 pt-4">
            <div className="font-display uppercase text-lg font-bold mb-3">Lower-Third (per clip)</div>
            <Field label="Template">
              <AutocompleteInput field="lower_third_template" value={t.lower_third_template || ""} onChange={(v) => setField("lower_third_template", v)} placeholder="{rider} · {sub_label}" className="field-input font-mono-tc" data-testid="tmpl-l3" />
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="label-tiny mr-1">Insert</span>
                {PLACEHOLDERS.map((p) => (
                  <button key={p} onClick={() => insertVar("lower_third_template", " " + p)} className="text-[10px] font-mono-tc text-orange-400 border border-stone-800 hover:border-orange-700 px-2 py-0.5" data-testid={`l3-var-${p}`}>{p}</button>
                ))}
              </div>
            </Field>
          </div>

          <div className="border-t border-stone-800 pt-4">
            <div className="font-display uppercase text-lg font-bold mb-3">Outro Card</div>
            <Field label="Main Title">
              <AutocompleteInput field="outro_text" value={t.outro_text || ""} onChange={(v) => setField("outro_text", v)} placeholder="THANK YOU" className="field-input" data-testid="tmpl-outro" />
            </Field>
            <Field label="Subtitle">
              <AutocompleteInput field="outro_subtext" value={t.outro_subtext || ""} onChange={(v) => setField("outro_subtext", v)} placeholder="See you at the next jackpot" className="field-input" data-testid="tmpl-outro-sub" />
            </Field>
            <Field label="Duration (seconds)">
              <input type="number" step="0.5" min="0" max="6" value={t.outro_duration} onChange={(e) => setField("outro_duration", parseFloat(e.target.value) || 0)} className="field-input !w-32" data-testid="tmpl-outro-dur" />
            </Field>
          </div>
        </div>
        <div className="px-6 pb-6 flex gap-2 sticky bottom-0 bg-stone-950 border-t border-stone-800 pt-4">
          <button onClick={onClose} className="btn-secondary flex-1" data-testid="tmpl-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex-1 inline-flex items-center justify-center gap-2" data-testid="tmpl-save">
            <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="label-tiny mb-1">{label}</div>
      {children}
    </div>
  );
}
